/**
 * Step 11b — Local write queue for active-trip mutations.
 *
 * Persists pending mutations to `localStorage` so that:
 *   • An action triggered while offline survives a reload / app close.
 *   • A reconnection replay can re-apply them sequentially in order.
 *
 * Scope (priority order, per 11b spec):
 *   1. arrival         — markera ankomst (centrala completeArrival path)
 *   2. skip_stop       — hoppa över aktivt stopp (notes + advance)
 *   3. reset_progress  — återställ etappstatus
 *   4. status_change   — pause / resume
 *   5. end_trip        — avsluta resa
 *
 * Dedupe model: each op carries a deterministic `dedupeKey`. Enqueueing an
 * op whose key already exists in the pending queue REPLACES the previous
 * one (so a user toggling pause/resume offline doesn't stack, and re-tapping
 * "Markera ankomst" on the same stop doesn't queue twice).
 *
 * Replay model is implemented in `useActiveTripSync` — this file only owns
 * persistence + dedupe semantics.
 */

const STORAGE_KEY = "vantastika.activeTripQueue.v1";

export type QueueOpType =
  | "arrival"
  | "skip_stop"
  | "reset_progress"
  | "status_change"
  | "end_trip";

export type QueueOpStatus = "pending" | "failed";

/**
 * 11c — classify why a replay failed, so the UI can give the user the right
 * advice instead of dumping a raw server error.
 *
 *  - "transient" → network / 5xx / unknown. Just retry.
 *  - "conflict"  → server state no longer matches the queued intent
 *                  (trip ended, stop deleted, RLS rejected, FK gone, etc).
 *                  Retrying alone won't help — the user needs to look at the
 *                  trip first.
 */
export type QueueOpErrorKind = "transient" | "conflict";

/** Discriminated payloads — keep them minimal but enough to replay safely. */
export type QueueOpPayload =
  | {
      type: "arrival";
      tripId: string;
      stopId: string;
      stopIndex: number;
      totalStops: number;
      alreadyStamped: boolean;
    }
  | {
      type: "skip_stop";
      tripId: string;
      stopId: string;
      stopIndex: number;
      totalStops: number;
      alreadyStamped: boolean;
    }
  | {
      type: "reset_progress";
      tripId: string;
    }
  | {
      type: "status_change";
      tripId: string;
      /** target status the user requested */
      nextStatus: "active" | "paused";
    }
  | {
      type: "end_trip";
      tripId: string;
    };

export interface QueueOp {
  /** Stable unique id for this queue entry (uuid-like). */
  id: string;
  /** Owner — never replay another user's queued op. */
  userId: string;
  /** Wall-clock when the op was queued. */
  enqueuedAt: string;
  /** Dedupe key — see file header. */
  dedupeKey: string;
  status: QueueOpStatus;
  /** Last replay error (when status === 'failed'). */
  lastError?: string | null;
  /** 11c — classification of the last failure, when status === 'failed'. */
  lastErrorKind?: QueueOpErrorKind | null;
  payload: QueueOpPayload;
}

const isBrowser = (): boolean =>
  typeof window !== "undefined" && typeof window.localStorage !== "undefined";

const safeRandomId = (): string => {
  try {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
      return (crypto as any).randomUUID();
    }
  } catch {
    // ignore
  }
  return `q_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
};

/** Read the entire queue (all users / all trips). */
export const readQueue = (): QueueOp[] => {
  if (!isBrowser()) return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((op): op is QueueOp =>
      op &&
      typeof op === "object" &&
      typeof op.id === "string" &&
      typeof op.userId === "string" &&
      typeof op.dedupeKey === "string" &&
      op.payload &&
      typeof op.payload.type === "string",
    );
  } catch {
    return [];
  }
};

/** Persist the queue. Best-effort — quota errors are swallowed. */
const writeQueue = (ops: QueueOp[]): void => {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ops));
    // Notify in-process listeners so the sync hook can re-render counters
    // immediately without polling. We also dispatch on `window` so other
    // tabs in the same origin can react to it (cross-tab via 'storage'
    // event still works natively for free).
    try {
      window.dispatchEvent(new CustomEvent("activeTripQueue:changed"));
    } catch {
      // ignore
    }
  } catch {
    // Quota / private mode — queue is best-effort.
  }
};

/** Pending ops belonging to a given user, in enqueue order. */
export const readPendingForUser = (userId: string | null | undefined): QueueOp[] => {
  if (!userId) return [];
  return readQueue().filter((op) => op.userId === userId);
};

/**
 * Build the dedupe key for a payload. Public so callers can probe whether
 * an equivalent op is already queued without going through enqueue.
 */
export const dedupeKeyFor = (payload: QueueOpPayload): string => {
  switch (payload.type) {
    case "arrival":
      return `${payload.tripId}:arrival:${payload.stopId}`;
    case "skip_stop":
      return `${payload.tripId}:skip:${payload.stopId}`;
    case "reset_progress":
      return `${payload.tripId}:reset`;
    case "status_change":
      // status keyed only by trip — toggling pause/resume offline collapses
      // to the *latest* requested state, not a stack of toggles.
      return `${payload.tripId}:status`;
    case "end_trip":
      return `${payload.tripId}:end`;
  }
};

/**
 * Enqueue an operation. If an op with the same dedupeKey is already pending
 * we REPLACE it (latest intent wins). Failed ops with the same key are also
 * replaced so a user manually retrying clears the old failure.
 * Returns the queued op.
 */
export const enqueueOp = (
  userId: string,
  payload: QueueOpPayload,
): QueueOp => {
  const key = dedupeKeyFor(payload);
  const next: QueueOp = {
    id: safeRandomId(),
    userId,
    enqueuedAt: new Date().toISOString(),
    dedupeKey: key,
    status: "pending",
    payload,
  };
  const all = readQueue();
  const filtered = all.filter(
    (op) => !(op.userId === userId && op.dedupeKey === key),
  );
  filtered.push(next);
  writeQueue(filtered);
  return next;
};

/** Remove a single op by id (used after successful replay). */
export const removeOp = (id: string): void => {
  const all = readQueue();
  writeQueue(all.filter((op) => op.id !== id));
};

/** Mark a single op as failed with a short error message. */
export const markOpFailed = (
  id: string,
  message: string,
  kind: QueueOpErrorKind = "transient",
): void => {
  const all = readQueue();
  const next = all.map((op) =>
    op.id === id
      ? {
          ...op,
          status: "failed" as const,
          lastError: message.slice(0, 240),
          lastErrorKind: kind,
        }
      : op,
  );
  writeQueue(next);
};

/** Reset failed ops back to pending (e.g. when user manually retries). */
export const retryFailedOpsForUser = (userId: string): void => {
  const all = readQueue();
  const next = all.map((op) =>
    op.userId === userId && op.status === "failed"
      ? { ...op, status: "pending" as const, lastError: null, lastErrorKind: null }
      : op,
  );
  writeQueue(next);
};

/** Clear all ops belonging to a user (e.g. logout / endTrip cleanup). */
export const clearQueueForUser = (userId: string): void => {
  const all = readQueue();
  writeQueue(all.filter((op) => op.userId !== userId));
};

/** Clear ops bound to a specific trip (e.g. trip ended successfully). */
export const clearQueueForTrip = (userId: string, tripId: string): void => {
  const all = readQueue();
  writeQueue(
    all.filter((op) => {
      if (op.userId !== userId) return true;
      const opTripId = (op.payload as any)?.tripId;
      return opTripId !== tripId;
    }),
  );
};

/** Subscribe to in-process queue changes. Returns an unsubscribe fn. */
export const subscribeQueue = (listener: () => void): (() => void) => {
  if (!isBrowser()) return () => {};
  const handler = () => listener();
  window.addEventListener("activeTripQueue:changed", handler);
  // Cross-tab updates via the native storage event.
  const storageHandler = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY) listener();
  };
  window.addEventListener("storage", storageHandler);
  return () => {
    window.removeEventListener("activeTripQueue:changed", handler);
    window.removeEventListener("storage", storageHandler);
  };
};

/**
 * 11c — does the user have a pending op queued against this exact stop?
 * Used by the timeline so a stop the user already tapped "Markera ankomst"
 * on (offline) can show a discreet "Väntar på synk" badge instead of
 * pretending the snapshot already reflects the change.
 */
export const hasPendingOpForStop = (
  userId: string | null | undefined,
  tripId: string | null | undefined,
  stopId: string | null | undefined,
): boolean => {
  if (!userId || !tripId || !stopId) return false;
  const ops = readPendingForUser(userId);
  return ops.some((op) => {
    if (op.status !== "pending") return false;
    const p = op.payload as any;
    return p?.tripId === tripId && p?.stopId === stopId;
  });
};

/**
 * 11c — heuristic: classify a server error string as conflict vs transient.
 * Conflict = server clearly disagrees with our queued intent (row gone,
 * RLS denied, FK violation, trip already completed, etc). Everything else
 * is treated as transient so the user can simply tap "Försök igen".
 *
 * This is intentionally simple — Supabase Postgrest errors all surface
 * as messages, so we match on the well-known shapes we see in practice.
 */
export const classifyReplayError = (raw: string | null | undefined): QueueOpErrorKind => {
  if (!raw) return "transient";
  const m = raw.toLowerCase();
  // Postgrest "no rows" after .eq() filter when nothing matches
  if (m.includes("pgrst116")) return "conflict";
  // RLS / permission denied
  if (m.includes("permission denied") || m.includes("row-level security")) return "conflict";
  // FK gone (e.g. trip was deleted)
  if (m.includes("violates foreign key") || m.includes("foreign key constraint")) return "conflict";
  // Trip lifecycle conflicts surfaced from our own helpers
  if (m.includes("trip already completed") || m.includes("active trip not found")) {
    return "conflict";
  }
  // 404-ish status from supabase-js
  if (m.includes("not found") && !m.includes("network")) return "conflict";
  return "transient";
};